import React, { useCallback, useEffect, useState } from 'react';
import LoginForm, { LoginFormProps } from '../LoginForm';
import PartialLoginWrapper from '../PartialLoginWrapper';
import RegistrationForm, { RegistrationFormProps } from '../RegistrationForm';
import { LoginRegisterEvents } from './LoginAndRegister.types';
import LoginRegisterWrapper, { FormProps, Panel } from './LoginRegisterWrapper';
import { PartialLoginCard } from './LoginRegisterWrapper.style';
import useTranslate from 'Hooks/useTranslate';
import useLocationChange from 'Hooks/useLocationChange';
import useIsRegistered from 'Hooks/useIsRegistered';
import { LOCAL_EVENTS } from 'Hooks/useCustomEvent';
import useApi from 'Hooks/useApi';
import { OverlayType } from 'Interfaces/overlayTracking';

export type LoginRegisterContainerProps = {
    forgotPasswordLink: string;
    redirectUrl: string;
    partial?: {
        name: string;
        email: string;
        onClearSession: () => void;
    };
    events?: LoginRegisterEvents;
};

const LoginRegisterContainer = ({
    forgotPasswordLink,
    redirectUrl,
    partial,
    events,
}: LoginRegisterContainerProps): JSX.Element => {
    const { tPlain } = useTranslate();
    const loginCTAText = tPlain('Login');
    const registerCTAText = tPlain('Register');
    const loginHeaderText = tPlain('Login_header');
    const registerHeaderText = tPlain('Registration_header');
    const [isLoading, setIsLoading] = useState(false);
    const [hasLoginApiError, setHasLoginApiError] = useState(false);
    const [hasRegisterApiError, setHasRegisterApiError] = useState(false);
    const { locationChange } = useLocationChange();
    const { login, registerUser } = useApi();
    const isRegistered = useIsRegistered();
    const [firstAndOpen, setFirstAndOpen] = useState<Panel>();
    const showRegister = () => {
        setFirstAndOpen(Panel.REGISTER);
    };

    const errorText = tPlain('Incorrect email or password_bella');

    useEffect(() => {
        const panel = isRegistered ? Panel.LOGIN : Panel.REGISTER;
        setFirstAndOpen(panel);

        document.addEventListener(LOCAL_EVENTS.AUTH_UNBLOCK_DECLINED, showRegister);
        return () => {
            document.removeEventListener(LOCAL_EVENTS.AUTH_UNBLOCK_DECLINED, showRegister);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onLoginSubmit = useCallback(
        async formInput => {
            if (isLoading) {
                return;
            }
            setIsLoading(true);
            const { email, password } = formInput;
            setHasLoginApiError(false);

            await login(
                { email, password },
                false,
                () => {
                    setHasLoginApiError(false);
                    setIsLoading(false);
                    if (events && events.onLoginSubmit) {
                        events.onLoginSubmit(true);
                    }
                    if (redirectUrl) {
                        locationChange(redirectUrl);
                    }
                },
                () => {
                    setIsLoading(false);
                    setHasLoginApiError(true);
                    if (events && events.onLoginSubmit) {
                        events.onLoginSubmit(false);
                    }
                    if (events && events.onErrorShown) {
                        events.onErrorShown(errorText);
                    }
                },
                OverlayType.Checkout
            );
        },
        [isLoading, login, redirectUrl, locationChange, setIsLoading, events, errorText]
    );

    const onRegisterSubmit = useCallback(
        async formInput => {
            if (isLoading) {
                return;
            }
            setIsLoading(true);
            const { email, password, acceptNewsletter } = formInput;
            setHasRegisterApiError(false);
            const onRegisterSuccess = () => {
                setHasRegisterApiError(false);
                setIsLoading(false);

                if (events && events.onRegisterSubmit) {
                    events.onRegisterSubmit(true);
                }

                if (redirectUrl) {
                    locationChange(redirectUrl);
                }
            };
            const onRegisterErrors = () => {
                setIsLoading(false);
                setHasRegisterApiError(true);
                if (events && events.onRegisterSubmit) {
                    events.onRegisterSubmit(false);
                }
                if (events && events.onErrorShown) {
                    events.onErrorShown(errorText);
                }
            };
            const payload = { email, password, isTermsAccepted: true, isNewsletterSubscribed: acceptNewsletter };
            await registerUser(payload, onRegisterSuccess, onRegisterErrors, OverlayType.Checkout);
        },
        [isLoading, setIsLoading, registerUser, locationChange, redirectUrl, events, errorText]
    );

    const loginProps: FormProps<LoginFormProps> = {
        headerText: loginHeaderText,
        component: LoginForm,
        buttonText: loginCTAText,
        formProps: {
            onSubmit: onLoginSubmit,
            forgotPasswordLink,
            onForgotPasswordClick: events?.onForgotPasswordClick,
            hasApiError: hasLoginApiError,
            setHasApiError: setHasLoginApiError,
            isLoading,
        },
    };
    const registerProps: FormProps<RegistrationFormProps> = {
        headerText: registerHeaderText,
        component: RegistrationForm,
        buttonText: registerCTAText,
        formProps: {
            onSubmit: onRegisterSubmit,
            hasApiError: hasRegisterApiError,
            setHasApiError: setHasRegisterApiError,
            isLoading,
        },
    };
    if (!firstAndOpen) {
        return <></>;
    }
    if (partial) {
        return (
            <PartialLoginCard>
                <PartialLoginWrapper
                    {...partial}
                    forgotPasswordLink={forgotPasswordLink}
                    redirectUrl={redirectUrl}
                    onShopWithAnotherAccount={events?.onShopWithAnotherAccountClick}
                    onForgotPasswordClick={events?.onForgotPasswordClick}
                />
            </PartialLoginCard>
        );
    }
    return (
        <LoginRegisterWrapper
            show={firstAndOpen}
            order={firstAndOpen}
            isLoading={isLoading}
            loginProps={loginProps}
            registerProps={registerProps}
        />
    );
};

export default LoginRegisterContainer;
